// Per-session KEK_membro management (D-191, D-193, AUTH-02).
//
// After successful login (password + TOTP), web-comite derives KEK_membro
// from the member's password via Argon2id (paridade D-25 denunciante) and
// keeps it in sessionStorage encrypted with a per-session SECRET — uma
// CryptoKey AES-GCM NÃO-EXTRAÍVEL gerada em memória no 1º store (fix 02.8-05).
//
// ⚠ POR QUE NÃO O JWT (bug 2026-06-22 corrigido): antes a chave de storage era
// HKDF(JWT). Mas o JWT TROCA (elevação biométrica pós-store) e ROTACIONA
// (sliding-refresh a cada request) → loadKekMembro com o token novo não decifrava
// a KEK cifrada sob o token velho → kek_decrypt_failed → falha_decrypt na leitura.
// O segredo de sessão (memory-only, não-extraível) é INDEPENDENTE do token: imune
// à elevação E à rotação. Doc canônico: docs/bugs/2026-06-22-kek-session-binding-*.
//
// Lifetime:
//   - storeKekMembro(kek) called once at login (gera/usa o segredo de sessão)
//   - loadKekMembro() called per /api/comite/cases/:id read (CaseDetailPage)
//   - clearKekMembro called on logout + session expiry (auth-client.ts) — zera o segredo
//
// ⚠ HARD-REFRESH → RE-LOGIN: o segredo é memory-only (variável module-level) → some
// no reload da aba → o ciphertext residual fica inerte → loadKekMembro trata como
// no_kek_in_session → re-login. Navegação client-side normal NÃO recarrega → o
// segredo persiste a sessão (todas as leituras de caso funcionam).
//
// SECURITY: KEK plaintext NEVER hits sessionStorage. Only AES-GCM ciphertext. O
// segredo (CryptoKey) é NÃO-EXTRAÍVEL e vive só no heap JS — uma extensão que leia
// o sessionStorage pega só o ciphertext (mais forte que o esquema-JWT, onde o JWT
// morava no mesmo sessionStorage).

import { deriveKeyMaterial, splitKeyMaterial } from '@aions/crypto';
import { bytesToBase64, base64ToBytes } from './bytes';
import { kekSessionReason, bloqueiaPorSonda, type KekSessionReason } from './kek-session-reason';

export { kekSessionReason, type KekSessionReason };

const STORAGE_KEY = 'aions:comite:kek_membro_encrypted:v2';

let memKekEncrypted: string | null = null;

// Segredo de sessão (fix 02.8-05): CryptoKey AES-GCM NÃO-EXTRAÍVEL, em memória
// module-level (paridade com memKekEncrypted). Gerado no 1º store da sessão,
// independente do token. Zerado em clearKekMembro. Some no reload → re-login.
let sessionKey: CryptoKey | null = null;

/** Gera (1ª vez) ou reusa o segredo de sessão não-extraível. NUNCA vai pro
 *  sessionStorage, NUNCA deriva do token. */
async function ensureSessionKey(): Promise<CryptoKey> {
  if (!sessionKey) {
    sessionKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
  }
  return sessionKey;
}

function readStorage(): string | null {
  if (memKekEncrypted) return memKekEncrypted;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) memKekEncrypted = raw;
    return raw;
  } catch {
    return null;
  }
}

function writeStorage(payload: string): void {
  memKekEncrypted = payload;
  try {
    sessionStorage.setItem(STORAGE_KEY, payload);
  } catch {
    // Pitfall 6: Safari incognito quota — keep memory copy only.
  }
}

function removeStorage(): void {
  memKekEncrypted = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Derives the 32-byte KEK_membro from password + salt via Argon2id
 * (paridade Phase 1 D-25 denunciante). Salt is the kekSalt persisted in
 * UserRecord (base64 of 32 bytes). Returns the kekRaw — caller is
 * responsible for storing it encrypted via storeKekMembro.
 */
export async function deriveKekMembro(password: string, kekSaltB64: string): Promise<Uint8Array> {
  // salt-first paridade Phase 1 D-25 (protocolo position).
  const material = await deriveKeyMaterial(kekSaltB64, password);
  const { kekRaw } = splitKeyMaterial(material);
  return kekRaw;
}

/**
 * Encrypts kekRaw with AES-GCM(key=segredo de sessão não-extraível) and stores in
 * sessionStorage (with in-mem fallback per Pitfall 6). O segredo é gerado no 1º
 * store da sessão — independente do token (fix 02.8-05).
 */
export async function storeKekMembro(kekRaw: Uint8Array): Promise<void> {
  const key = await ensureSessionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new Uint8Array(kekRaw));
  const combined = new Uint8Array(12 + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), 12);
  writeStorage(bytesToBase64(combined));
}

/**
 * Decrypts and returns the 32-byte KEK_membro from sessionStorage.
 * Throws `kek-member-session: no_kek_in_session` if absent OR se o segredo de
 * sessão foi perdido (hard-refresh — re-login). `kek-member-session:
 * kek_decrypt_failed` se o ciphertext não abre com o segredo atual.
 */
export async function loadKekMembro(): Promise<Uint8Array> {
  const stored = readStorage();
  if (!stored) throw new Error('kek-member-session: no_kek_in_session');
  // Sem o segredo de sessão (hard-refresh perdeu a CryptoKey memory-only) o
  // ciphertext residual é inerte → tratar como "sem KEM na sessão" → re-login.
  if (!sessionKey) throw new Error('kek-member-session: no_kek_in_session');
  const combined = base64ToBytes(stored);
  const iv = combined.slice(0, 12);
  const ct = combined.slice(12);
  try {
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, sessionKey, ct);
    return new Uint8Array(pt);
  } catch {
    throw new Error('kek-member-session: kek_decrypt_failed');
  }
}

/**
 * SONDA — a KEK está viva nesta sessão? Feita para rodar no MOUNT das telas de
 * ESCRITA, ANTES de o membro digitar: descobrir a sessão morta só no clique do
 * "emitir" custa o retrabalho inteiro (ele redigita e só então leva erro).
 *
 * NÃO retém nem devolve a KEK: carrega, zera no finally e devolve só o motivo
 * (`null` = viva). Exceção que não é de sessão PROPAGA — quem chama decide.
 */
export async function probeKekMembro(): Promise<KekSessionReason | null> {
  let kekRaw: Uint8Array | null = null;
  try {
    kekRaw = await loadKekMembro();
    return null;
  } catch (e) {
    const reason = kekSessionReason(e);
    if (reason === null) throw e;
    return reason;
  } finally {
    kekRaw?.fill(0);
  }
}

/** Wiring da regra `bloqueiaPorSonda` com a sonda real. A REGRA (e o motivo de ela
 *  não ser gate) vive em `kek-session-reason.ts`, onde é testada por injeção. */
export async function kekMortaNestaSessao(): Promise<boolean> {
  return bloqueiaPorSonda(probeKekMembro);
}

/** Clears the in-memory cache, sessionStorage AND the session secret (D-191
 *  logout/expiry; fix 02.8-05 — sem o segredo o ciphertext residual é inerte). */
export function clearKekMembro(): void {
  sessionKey = null;
  removeStorage();
}
